import { useEffect, useState } from 'react';
import { JUCO_YEARS, type JucoYear } from '../../../../utils/roster-class';
import type { JucoScope } from '../../../../types/batch-extraction';
import { ModalCloseButton } from '../../../../components/ModalCloseButton';
import './BulkJucoYearModal.css';

type ScopeCounts = {
  /** How many JUCO-marked records in scope currently have no JC year. */
  blankCount: number;
  /** How many JUCO-marked records are in scope in total. */
  markedCount: number;
};

type Props = ScopeCounts & {
  /**
   * The same counts across every page of the batch. Omit to offer the current page only.
   */
  allPages?: ScopeCounts & {
    /** True while a page is still extracting; the server refuses the batch scope then. */
    disabled: boolean;
  };
  /** Names the current scope: "This page", or "This team" when a team tab is selected. */
  pageScopeLabel?: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (jucoYear: JucoYear, onlyBlank: boolean, scope: JucoScope) => void;
};

/**
 * Fills one JC year across a page's JUCO-marked records, or across every page of the batch.
 *
 * This is required, not a convenience. Some roster pages carry no class column at all, so marking
 * one JUCO leaves every row without a year and the commit gate blocks the whole import. With no
 * bulk fill there is no way out of that except editing each row by hand.
 */
export const BulkJucoYearModal = ({
  blankCount,
  markedCount,
  allPages,
  pageScopeLabel = 'This page',
  submitting,
  onClose,
  onSubmit,
}: Props) => {
  const [jucoYear, setJucoYear] = useState<JucoYear>('1');
  // Default to the blanks only: the years the parser read off the roster are better evidence
  // than one value typed over the whole page.
  const [onlyBlank, setOnlyBlank] = useState(true);
  // Start on the page the reviewer is looking at, unless it has nothing to fill and the rest of
  // the batch does.
  const [scope, setScope] = useState<JucoScope>(() =>
    markedCount === 0 && allPages && !allPages.disabled && allPages.markedCount > 0 ? 'batch' : 'page'
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // A page that starts extracting while the dialog is open drops the batch option.
  const effectiveScope: JucoScope = scope === 'batch' && allPages && !allPages.disabled ? 'batch' : 'page';
  const counts = effectiveScope === 'batch' && allPages ? allPages : { blankCount, markedCount };
  const affected = onlyBlank ? counts.blankCount : counts.markedCount;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || affected === 0) return;
    onSubmit(jucoYear, onlyBlank, effectiveScope);
  };

  const scopeOptions: { value: JucoScope; label: string; disabled: boolean }[] = [
    { value: 'page', label: pageScopeLabel, disabled: false },
    { value: 'batch', label: 'All pages', disabled: allPages?.disabled ?? true },
  ];

  return (
    <div className="bulk-juco-modal__overlay">
      <div
        className="bulk-juco-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-juco-modal-title"
      >
        <div className="bulk-juco-modal__header">
          <h3 id="bulk-juco-modal-title" className="bulk-juco-modal__title">
            Fill JC year
          </h3>
          <ModalCloseButton onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="bulk-juco-modal__form">
          {allPages && (
            <>
              <span className="bulk-juco-modal__label" id="bulk-juco-modal-scope-label">
                Apply to
              </span>
              <div
                className="bulk-juco-modal__years"
                role="group"
                aria-labelledby="bulk-juco-modal-scope-label"
              >
                {scopeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`bulk-juco-modal__year${
                      effectiveScope === option.value ? ' bulk-juco-modal__year--selected' : ''
                    }`}
                    aria-pressed={effectiveScope === option.value}
                    disabled={submitting || option.disabled}
                    onClick={() => setScope(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {allPages.disabled && (
                <p className="bulk-juco-modal__summary">
                  All pages is available once every page finishes extracting.
                </p>
              )}
            </>
          )}

          <span className="bulk-juco-modal__label" id="bulk-juco-modal-year-label">
            Year of junior college
          </span>
          <div
            className="bulk-juco-modal__years"
            role="group"
            aria-labelledby="bulk-juco-modal-year-label"
          >
            {JUCO_YEARS.map((year) => (
              <button
                key={year}
                type="button"
                className={`bulk-juco-modal__year${
                  jucoYear === year ? ' bulk-juco-modal__year--selected' : ''
                }`}
                aria-pressed={jucoYear === year}
                disabled={submitting}
                onClick={() => setJucoYear(year)}
              >
                JC {year}
              </button>
            ))}
          </div>

          <label className="bulk-juco-modal__only-blank">
            <input
              type="checkbox"
              checked={onlyBlank}
              disabled={submitting}
              onChange={(e) => setOnlyBlank(e.target.checked)}
            />
            <span>Only rows with no JC year ({counts.blankCount})</span>
          </label>

          <p className="bulk-juco-modal__summary">
            {affected === 0
              ? 'No rows would change.'
              : `Sets JC ${jucoYear} on ${affected} ${affected === 1 ? 'row' : 'rows'}${
                  effectiveScope === 'batch' ? ' across all pages' : ''
                }.`}
          </p>

          <div className="bulk-juco-modal__actions">
            <button
              type="button"
              className="bulk-juco-modal__button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bulk-juco-modal__button bulk-juco-modal__button--primary"
              disabled={submitting || affected === 0}
            >
              {submitting ? 'Saving…' : 'Fill'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
